<template>
  <div class="space-y-2">
    <label :for="id" class="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-dark-300">
      <span>{{ label }}</span>
      <span v-if="required && !modelValue" class="text-xs font-normal text-gray-500 dark:text-gray-400">(Required)</span>
      <i v-else-if="required && modelValue" class="fas fa-check-circle text-success-600 dark:text-success-400 text-xs"></i>
    </label>
    <select
      :id="id"
      :value="modelValue"
      @input="$emit('update:modelValue', $event.target.value)"
      class="mt-1 block w-full px-4 py-2 text-base border border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100 border-gray-300 dark:border-dark-600"
      :class="{ 'border-red-300 dark:border-red-500 focus:border-red-500 focus:ring-red-500': error }"
      :required="required"
      :disabled="disabled"
      @change="handleChange"
    >
      <option value="" disabled class="bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100">Select an order type</option>
      <option
        v-for="type in displayedOrderTypes"
        :key="type.id"
        :value="type.id"
        class="py-2 px-4 hover:bg-blue-50 dark:hover:bg-blue-900/30 bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100"
      >
        {{ type.name }}
      </option>
    </select>
    <p v-if="error" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ error }}</p>
    
    <!-- Order Type Details -->
    <div v-if="selectedType" class="mt-2 text-sm space-y-1">
      <p class="text-gray-600 dark:text-dark-400">{{ selectedType.description }}</p>
      <div class="flex flex-wrap gap-2">
        <span v-if="selectedType.requires_approval" 
              class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
          Requires Approval
        </span>
      </div>
    </div>

    <!-- Validation Warnings -->
    <div v-if="validationWarnings.length > 0" class="mt-2 space-y-1">
      <div v-for="(warning, index) in validationWarnings" 
           :key="index"
           class="text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-600 px-3 py-2 rounded-lg">
        {{ warning.message }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useApi } from '@/composables/useApi';

const props = defineProps({
  modelValue: {
    type: [Number, String],
    default: ''
  },
  label: {
    type: String,
    default: 'Order Type'
  },
  id: {
    type: String,
    default: 'orderType'
  },
  required: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  availableTypes: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['update:modelValue', 'change', 'validation']);

const api = useApi();
const orderTypes = ref([]);
const validationWarnings = ref([]);

const selectedType = computed(() => {
  if (!props.modelValue) return null;
  return orderTypes.value.find(type => type.id === Number(props.modelValue));
});

const displayedOrderTypes = computed(() => {
  if (!props.availableTypes || props.availableTypes.length === 0) {
    return orderTypes.value;
  }
  
  // If availableTypes contains strings (type names), filter by name
  if (typeof props.availableTypes[0] === 'string') {
    return orderTypes.value.filter(type => 
      props.availableTypes.includes(type.name)
    );
  }
  
  // If availableTypes has order type objects, use ID-based filtering
  if (props.availableTypes.some(t => t.id)) {
    return orderTypes.value.filter(type => 
      props.availableTypes.some(availableType => availableType.id === type.id)
    );
  }
  
  return orderTypes.value;
});

const handleChange = async (event) => {
  const value = event.target.value;
  emit('change', value);
  
  if (value) {
    const type = orderTypes.value.find(t => t.id === Number(value));
    if (type) {
      emit('validation', {
        requiresApproval: type.requires_approval
      });
    }
  }
};

const loadOrderTypes = async () => {
  try {
    const response = await api.get('/order-types');
    if (response.data && Array.isArray(response.data)) {
      orderTypes.value = response.data;
    }
  } catch (error) {
    console.error('Error loading order types:', error);
  }
};

onMounted(() => {
  loadOrderTypes();
});
</script> 